import { SITE_NAME, SITE_ORIGIN } from './site.js';
/**
 * Alt text for a post's hero image, wherever it's rendered (article page, blog
 * index, admin list). Authors can describe the photo per post; a blank field
 * falls back to the title, which is what every card and hero used before the
 * field existed.
 */
export function blogCoverAlt(post) {
    return post.cover_image_alt?.trim() || post.title;
}
function firstNonEmpty(...values) {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed)
            return trimmed;
    }
    return undefined;
}
export function absoluteUrl(url, origin) {
    if (/^https?:\/\//i.test(url) || url.startsWith('//'))
        return url;
    return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}
/**
 * Google's Article guidance wants a real person or organization, and the
 * author's email is not ours to publish, so fall back to the house name.
 */
export function blogAuthorName(post) {
    return post.author?.full_name?.trim() || SITE_NAME;
}
/**
 * Everything the article page's `<head>` needs, resolved through the
 * SEO-title/meta-description fallbacks once so the meta tags, the Twitter card,
 * and the JSON-LD can never disagree with each other.
 */
export function buildBlogPostSeo(post, origin = SITE_ORIGIN) {
    const headline = firstNonEmpty(post.seo_title, post.title) ?? post.title;
    const description = firstNonEmpty(post.meta_description, post.excerpt);
    const canonical = `${origin}/blog/${post.slug}`;
    const image = post.cover_image_url
        ? absoluteUrl(post.cover_image_url, origin)
        : undefined;
    const imageAlt = image ? blogCoverAlt(post) : undefined;
    const authorName = blogAuthorName(post);
    const publishedTime = firstNonEmpty(post.published_at, post.created_at);
    const modifiedTime = firstNonEmpty(post.updated_at) ?? publishedTime;
    const metaTags = [
        ...(description ? [{ attr: 'name', key: 'description', content: description }] : []),
        { attr: 'property', key: 'og:type', content: 'article' },
        { attr: 'property', key: 'og:title', content: headline },
        ...(description
            ? [{ attr: 'property', key: 'og:description', content: description }]
            : []),
        { attr: 'property', key: 'og:url', content: canonical },
        ...(image ? [{ attr: 'property', key: 'og:image', content: image }] : []),
        ...(imageAlt ? [{ attr: 'property', key: 'og:image:alt', content: imageAlt }] : []),
        { attr: 'property', key: 'og:site_name', content: SITE_NAME },
        { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
        { attr: 'name', key: 'twitter:title', content: headline },
        ...(description
            ? [{ attr: 'name', key: 'twitter:description', content: description }]
            : []),
        ...(image ? [{ attr: 'name', key: 'twitter:image', content: image }] : []),
        ...(imageAlt
            ? [{ attr: 'name', key: 'twitter:image:alt', content: imageAlt }]
            : []),
        ...(publishedTime
            ? [{ attr: 'property', key: 'article:published_time', content: publishedTime }]
            : []),
        ...(modifiedTime
            ? [{ attr: 'property', key: 'article:modified_time', content: modifiedTime }]
            : []),
        { attr: 'property', key: 'article:author', content: authorName },
    ];
    return {
        documentTitle: `${headline} | ${SITE_NAME}`,
        headline,
        description,
        canonical,
        image,
        imageAlt,
        authorName,
        publishedTime,
        modifiedTime,
        metaTags,
    };
}
